<!-- App.vue -->

<template>

  <div id="app">
    <template v-if="user && !isPublicDocumentPage">
      <AppNavbar @showAlert="showAlert" />
    </template>

    <div v-if="errorVisible" class="container pt-3 pb-3">
      <div class="alert alert-danger text-center mb-0">
        <p>There was a critical error and this page didn't fully load. If you did a change, it might not have been saved.</p>
        <p><a href="" class="btn btn-sm btn-danger" @click="refreshPage">Refresh this page</a></p>
        <span class="small">{{ errorMessage }}</span>
      </div>
    </div>

    <div v-else>
      <div v-if="user && appDataErrors.length" class="container pt-3">
        <div class="alert alert-warning d-flex align-items-center justify-content-between gap-3 mb-0" role="alert">
          <span>
            Some supporting data didn't load ({{ appDataErrorLabels }}). The rest of the app is still available.
          </span>
          <button
            type="button"
            class="btn btn-sm btn-warning text-nowrap"
            :disabled="appDataFetchInProgress"
            @click="retryFailedAppData"
          >
            {{ appDataFetchInProgress ? 'Retrying…' : 'Retry failed data' }}
          </button>
        </div>
      </div>

      <bootstrap-toast
        v-if="alertVisible"
        :type="alertType"
        :show="alertVisible"
        :message="alertMessage"
        @update:show="alertVisible = $event"
      />

      <div :class="getBodyClass" class="custom-container">
        <router-view @showAlert="showAlert" @showError="showError" />
      </div>
    </div>

    <!-- Footer for app pages -->
    <!-- <template v-if="!isQuoteDetailsPage">
      <div class="custom-container mt-4 mb-5 pt-2 border-top small text-muted">
        &copy; Language Buró OÜ
      </div>
    </template> -->
  </div>

</template>

<script>
import { mapGetters } from 'vuex';

import AppNavbar from '@/components/AppNavbar.vue';
import BootstrapToast from '@/components/BootstrapToast.vue';

export default {
  name: 'App',
  components: {
    AppNavbar,
    BootstrapToast
  },

  data() {
    return {
      alertVisible: false,
      alertType: '',
      alertMessage: '',
      errorMessage: '',

      user: null,
      loading: false,
      appDataFetched: false,
      appDataFetchInProgress: false,
      appDataErrors: [],
      clerkUnsubscribe: null,
    };
  },

  computed: {
    ...mapGetters([
        'fetchedOrderDetails',
        'allTeam',
        'allPMs',
        'allCompanies',
        'allContacts',
        'allLanguagePairs',
        'allWorkableVendors',
        'allVendors',
        'allLanguages',
        'allCountries',

        'getProperty',
      ]),

    errorVisible() {
      return this.errorMessage !== '';
    },

    appDataErrorLabels() {
      return this.appDataErrors.map(({ request }) => request.label).join(', ');
    },

    getBodyClass() {
      switch (this.$route.name) {
        case 'Quote Details':
          return 'quote-details';
        case 'Accounts Payable Invoice':
          return 'accounts-payable-invoice';
        default:
          return 'app'; // Default class
      }
    },

    isPublicDocumentPage() {
      return this.$route.name === 'Quote Details'
        || this.$route.name === 'Accounts Payable Invoice'
        || this.$route.path.startsWith('/quotes/')
        || this.$route.path.startsWith('/accounts-payable/invoices/');
    },
  },

  watch: {
    $route() {
      document.body.className = this.getBodyClass;

      if (this.user && this.$route.name !== 'Login' && !this.isPublicDocumentPage) {
        this.fetchAppData();
      }
    },
  },

  methods: {
    refreshPage() {
      window.location.reload();
    },

    // App: Alerts
    showAlert(type, message) {
      this.alertType = type;
      this.alertMessage = message;
      this.alertVisible = false;
      this.$nextTick(() => {
        this.alertVisible = true;
      });

      // console.log(`Received alert: type=${type}, message=${message}`); // debug
    },

    showError(errorMessage) {
      this.errorMessage = errorMessage;
    },

    getAppDataRequests() {
      return [
        {
          label: 'Team',
          payload: {
            tableName: 'Team',
            filter: '{Active?} = "Yes"',
            stateVariable: 'team',
            fetchingStateVariable: 'fetchingTeam',
          },
        },
        {
          label: 'Project managers',
          payload: {
            tableName: 'Team',
            filter: 'AND({Active?} = "Yes", {PM?} = "Yes")',
            stateVariable: 'PMs',
            fetchingStateVariable: 'fetchingPMs',
          },
        },
        {
          label: 'Companies',
          payload: {
            tableName: 'Companies',
            stateVariable: 'companies',
            fetchingStateVariable: 'fetchingCompanies',
          },
        },
        {
          label: 'Contacts',
          payload: {
            tableName: 'Contacts',
            stateVariable: 'contacts',
            fetchingStateVariable: 'fetchingContacts',
          },
        },
        {
          label: 'Language pairs',
          payload: {
            tableName: 'Language Pairs',
            stateVariable: 'languagePairs',
            fetchingStateVariable: 'fetchingLanguagePairs',
          },
        },
        {
          label: 'Vendors',
          payload: {
            tableName: 'Vendors',
            stateVariable: 'vendors',
            fetchingStateVariable: 'fetchingVendors',
          },
        },
        {
          label: 'Vendor services',
          payload: {
            tableName: 'Vendor Services',
            stateVariable: 'vendorsServices',
            fetchingStateVariable: 'fetchingVendorsServices',
          },
        },
        {
          label: 'Languages',
          payload: {
            tableName: 'Languages',
            stateVariable: 'languages',
            fetchingStateVariable: 'fetchingLanguages',
          },
        },
        {
          label: 'Countries',
          payload: {
            tableName: 'Countries',
            stateVariable: 'countries',
            fetchingStateVariable: 'fetchingCountries',
          },
        },
        {
          label: 'Services',
          payload: {
            tableName: 'Services',
            stateVariable: 'services',
            fetchingStateVariable: 'fetchingServices',
          },
        },
        {
          label: 'Units',
          payload: {
            tableName: 'Units',
            stateVariable: 'units',
            fetchingStateVariable: 'fetchingUnits',
          },
        },
        {
          label: 'Specialities',
          payload: {
            tableName: 'Specialities',
            stateVariable: 'specialities',
            fetchingStateVariable: 'fetchingSpecialities',
          },
        },
        {
          label: 'Industries',
          payload: {
            tableName: 'Industries',
            stateVariable: 'industries',
            fetchingStateVariable: 'fetchingIndustries',
          },
        },
      ];
    },

    async fetchAppData(requests = null) {
      const isRetry = Array.isArray(requests);

      if (!this.user
        || this.$route.name === 'Login'
        || this.isPublicDocumentPage
        || this.appDataFetchInProgress
        || (!isRetry && this.appDataFetched)) {
        return;
      }

      const requestsToFetch = requests || this.getAppDataRequests();

      if (!isRetry) {
        this.appDataFetched = true;
      }

      this.appDataFetchInProgress = true;

      try {
        const results = await Promise.allSettled(
          requestsToFetch.map(request => this.$store.dispatch('fetchRecords', request.payload))
        );

        this.appDataErrors = results.reduce((errors, result, index) => {
          if (result.status === 'rejected') {
            const request = requestsToFetch[index];
            console.error(`Failed to preload ${request.label}:`, result.reason);
            errors.push({
              request,
              errorMessage: result.reason?.message || 'Unknown error',
            });
          }

          return errors;
        }, []);

      } finally {
        this.appDataFetchInProgress = false;
      }
    },

    async retryFailedAppData() {
      const failedRequests = this.appDataErrors.map(({ request }) => request);

      if (failedRequests.length > 0) {
        await this.fetchAppData(failedRequests);
      }
    },
  },

  async created() {
    await this.$router.isReady();

    document.body.className = this.getBodyClass;

    this.user = this.$clerk.user;
    this.clerkUnsubscribe = this.$clerk.addListener(({ user }) => {
      const wasAuthenticated = Boolean(this.user);
      this.user = user || null;

      if (this.user && !wasAuthenticated) {
        this.fetchAppData();
      }

      if (!this.user) {
        this.appDataFetched = false;
        this.appDataErrors = [];
      }
    });

    await this.fetchAppData();
  },

  beforeUnmount() {
    if (this.clerkUnsubscribe) {
      this.clerkUnsubscribe();
    }
  },

};
</script>
